import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { APIS } from '../../config/apis';
import { ALERT_TYPE, ShowToastMessage, authActionLogin, useForm } from '@laramie/ui';
import { VALIDATION_MESSAGE } from '../../consts/validationMessage';
import encrypt from '../../utils/encrypt';
import HeaderBasic from '../../components/layout/HeaderBasic';
import { standardRequest } from '../../utils/requests';
import { useFormErrors, useManagedContext } from '../../components/hooks';
import { FormError, InputSecret } from '../../components/common';
import { requestPasswordRecovery } from '../RecoverPasswordView';
import { LOGOUT_DONE_PARAM, isLoggingOut, logoutFinished, startSsoLogin, ssoEnabled, ssoLogout } from '../../utils/sso';


function LoginView() {

    const { setIsLoading } = useManagedContext()
    const [searchParams] = useSearchParams()
    const ssoError = searchParams.get('sso_error')
    const logoutError = searchParams.get('logout_error')

    // Keycloak vuelve acá con este parámetro recién cuando cerró su sesión.
    if (searchParams.get(LOGOUT_DONE_PARAM) === 'done') logoutFinished()

    // Si Keycloak ya nos devolvió un error, o no se pudo cerrar su sesión, no volver
    // a redirigir solo: quedaría en bucle o volvería a entrar sin pedir contraseña.
    // Con una salida a medio hacer, se termina de cerrar la sesión de Keycloak.
    useEffect(() => {
        if (!ssoEnabled() || ssoError || logoutError) return
        if (isLoggingOut()) ssoLogout()
        else startSsoLogin().catch(e => ShowToastMessage(ALERT_TYPE.ALERT_ERROR, e.message))
    }, [ssoError, logoutError])

    const onRetrySso = () => {
        setIsLoading(true)
        startSsoLogin({ forceLogin: true }).catch(e => {
            setIsLoading(false)
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, e.message)
        })
    }

    const onRetryLogout = () => {
        setIsLoading(true)
        ssoLogout()
    }


    const [ formValues, formHandle ] = useForm({
        username: '',
        password: ''
    });

    const dispatch = useDispatch();

    const getUsuarioErrors = () => {
        const errors = {}

        if (formValues.username.length === 0) errors.username = VALIDATION_MESSAGE.REQUIRED

        return errors
    }

    const getErrors = () => {
        const errors = getUsuarioErrors()

        if (formValues.password.length === 0) errors.password = VALIDATION_MESSAGE.REQUIRED

        return errors
    }

    const { errorOf, validate } = useFormErrors(getErrors)
    const { errorOf: errorOfRecuperacion, validate: validateRecuperacion } = useFormErrors(getUsuarioErrors)

    const errorOfUsuario = () => errorOf('username') ?? errorOfRecuperacion('username')

    if (ssoEnabled()) {
        return (
            <div>
                <HeaderBasic showMenu={false} />
                <div className='bg-img'>
                    <div className="login-container">
                        <section className='lg-c'>
                            {logoutError ? (
                                <>
                                    <FormError message={`No se pudo cerrar la sesión: ${logoutError}`} />
                                    <div className="mb-3 lg-btn">
                                        <button className="btn btn-main" onClick={onRetryLogout}>Reintentar cerrar sesión</button>
                                    </div>
                                </>
                            ) : ssoError ? (
                                <>
                                    <FormError message={ssoError} />
                                    <div className="mb-3 lg-btn">
                                        <button className="btn btn-main" onClick={onRetrySso}>Volver a intentar</button>
                                    </div>
                                </>
                            ) : (
                                <p>Redirigiendo al inicio de sesión…</p>
                            )}
                        </section>
                    </div>
                </div>
            </div>
        )
    }

    const handleClick = () => {
        if (!validate()) return

        Login();
    }

    const onClickRecoverPassword = () => {
        if (!validateRecuperacion()) return

        setIsLoading(true)
        requestPasswordRecovery(formValues.username)
        .finally(() => setIsLoading(false))
    }

    const Login = () => {
        setIsLoading(true)

        const body = {
            username: formValues.username,
            password: encrypt(formValues.password),
        }

        standardRequest('POST', APIS.URLS.LOGIN, body, { format: 'text' })
        .then(token => dispatch(authActionLogin(formValues.username, token)))
        .catch(e => {
            if (e.message) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, e.message)
            else ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando solicitud')
        })
        .finally(() => setIsLoading(false))
    };

    return (
    <div>
        <HeaderBasic showMenu={false} />
        <div className='bg-img'>
                
            <div className="login-container">
                <section className='lg-c'>

                    <div>
                        <label htmlFor="username" className="form-label">Usuario/a</label>
                        <input 
                            name="username"
                            type="text"
                            placeholder=""
                            className={`form-control ${errorOfUsuario() ? 'is-invalid' : ''}`}
                            value={ formValues.username }
                            onChange={ formHandle }
                        />
                        <FormError message={errorOfUsuario()} />
                    </div>
                    <div>
                        <InputSecret
                            title="Contraseña"
                            name="password"
                            value={formValues.password}
                            onChange={formHandle}
                            onPressEnter={handleClick}
                            invalid={!!errorOf('password')}
                        />
                        <FormError message={errorOf('password')} />
                    </div>
                    <a href='#' className="login-link" onClick={onClickRecoverPassword}>¿Olvidó su contraseña?</a>
                    <div className="mb-3 lg-btn">
                        <button className="btn btn-main" onClick={ handleClick }>Ingresar</button>
                    </div>

                </section>
            </div>

        </div>
    </div>
    )
}

export default LoginView;
